import { useEffect, useState } from 'react';
import { Box, Button, makeStyles, TextField } from '@material-ui/core';
import { MinusIcon, PlusIcon } from 'pages/quotes/icons';
import { TextColor } from '@setvi/shared/enums';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center'
  },
  button: {
    borderRadius: 120,
    padding: 4,
    minWidth: 28,
    maxHeight: 28,
    minHeight: 'auto'
  },
  textField: {
    minWidth: 68,
    maxWidth: 88,
    '& .MuiOutlinedInput-root': { borderRadius: 99 },
    '& .MuiInputBase-input': {
      textAlign: 'center',
      fontWeight: 600,
      padding: '20px 12px',
      fontSize: 16,
      color: '#000',
      backgroundColor: '#F8F9FA',
      borderRadius: 99,
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      },
      '&[type=number]': { MozAppearance: 'textfield' }
    }
  }
}));

interface QuantityCellProps {
  quantity: number;
  /** Step value — when minimumQuoteQuantity is set, +/- moves by this amount */
  step?: number;
  onChange: (value: number) => void;
}

export const QuantityCell = ({
  quantity,
  step = 1,
  onChange
}: QuantityCellProps) => {
  const classes = useStyles();
  const [local, setLocal] = useState<number>(quantity);

  useEffect(() => {
    setLocal(quantity);
  }, [quantity]);

  const commit = (next: number) => {
    const safe = Number.isFinite(next) && next >= 0 ? next : 0;
    setLocal(safe);
    onChange(safe);
  };

  const handleMinus = () => {
    // At or below step, going down deselects (0). Otherwise subtract step.
    commit(local <= step ? 0 : local - step);
  };

  const handlePlus = () => {
    commit(local + step);
  };

  return (
    <Box className={classes.container} onClick={e => e.stopPropagation()}>
      <Button
        className={classes.button}
        variant="text"
        onClick={handleMinus}
        disabled={local <= 0}>
        <MinusIcon
          fill={local <= 0 ? 'rgba(0, 0, 0, 0.26)' : TextColor.DarkGrey}
        />
      </Button>
      <TextField
        className={classes.textField}
        value={local}
        onChange={e => {
          const raw = e.target.value.replace(/[^\d]/g, '');
          commit(raw === '' ? 0 : parseInt(raw, 10));
        }}
        variant="outlined"
        size="small"
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          style: { color: local === 0 ? '#AFBAC6' : '#000' }
        }}
      />
      <Button className={classes.button} variant="text" onClick={handlePlus}>
        <PlusIcon fill={TextColor.DarkGrey} />
      </Button>
    </Box>
  );
};
